import { EVENTS } from '../constants';
import { type IChatMethods } from './useChatMethods';
import useEventListener from './useEventListener';

const SIZES = {
  SM: 576,
  MD: 768,
  LG: 992,
  XL: 1200,
  XXL: 1700,
};

/**
 * Hook that automatically hides the chat on mobile devices.
 * @param {React.Ref<IChatMethods>} ref The chat methods ref object.
 * @param {boolean} autoHide Whether the chat should be hidden on mobile devices.
 * @returns {void}
 * @example
 * const chatMethodsRef = useChatMethods();
 * useAutoHideOnMobile(chatMethodsRef, true);
 */
const useAutoHideOnMobile = (ref: React.Ref<IChatMethods>, autoHide: boolean) => {
  const isMobile = () => window.innerWidth <= SIZES.SM;

  const executeChatMethod = (method: keyof IChatMethods) => {
    if (autoHide && ref && 'current' in ref && ref.current?.[method]) {
      ref.current[method]();
    }
  };

  const handleVisibility = () => {
    executeChatMethod(isMobile() ? 'hide' : 'show');
  };

  const handleShowOnOpen = () => {
    isMobile() && executeChatMethod('show');
  };

  const handleHideOnClose = () => {
    isMobile() && executeChatMethod('hide');
  };

  useEventListener('resize', handleVisibility, true);
  useEventListener(EVENTS.ON_CHAT_INIT, handleVisibility, true);
  useEventListener(EVENTS.ON_CHAT_OPEN, handleShowOnOpen);
  useEventListener(EVENTS.ON_CHAT_CLOSE, handleHideOnClose);
};

export default useAutoHideOnMobile;
